import axios from 'axios'
import type {
  AxiosError,
  AxiosInstance,
  AxiosRequestHeaders,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios'
// @ts-ignore
import qs from 'qs'

import { config } from './config'
import MockAdapter from 'axios-mock-adapter'
import { commonRefreshToken } from '~/apis/auth'
import type { RefreshTokenData } from '~/apis/auth/type'
import { logoutAndCleanData } from '~/common/utils'
import { useTokenValidation } from '~/composables/useTokenValidation'
import { devConsole } from '~/common/devConsole'

const {
  result_code,
  token_missing_code,
  token_expire_code,
  token_error_code,
  token_error_reject_code,
  reject_code,
  withe_result_codes,
} = config
const COOKIE_EXPIRE = 336 * 60 * 60 * 1000

export const PATH_URL = import.meta.env.VITE_BASE_URL

// Create an axios instance
const service: AxiosInstance = axios.create({
  baseURL: PATH_URL, // api 的 base_url
  timeout: config.request_timeout, // 请求超时时间
})
const serviceMock: AxiosInstance = axios.create({
  baseURL: 'http://localhost:3000', // api 的 base_url
  timeout: config.request_timeout, // 请求超时时间
})
export const mock = new MockAdapter(serviceMock)

// 토큰 갱신 Promise 공유 변수
let refreshTokenPromise: Promise<RefreshTokenData> | null = null

//console.log(PATH_URL)
// request拦截器
service.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    if (
      config.method === 'post' &&
      (config.headers as AxiosRequestHeaders)['Content-Type'] ===
        'application/x-www-form-urlencoded'
    ) {
      config.data = qs.stringify(config.data)
    }

    // get参数编码
    if (config.method === 'get' && config.params) {
      let url = config.url as string
      url += '?'
      const keys = Object.keys(config.params)
      for (const key of keys) {
        if (config.params[key] !== void 0 && config.params[key] !== null) {
          url += `${key}=${encodeURIComponent(config.params[key])}&`
        }
      }
      url = url.substring(0, url.length - 1)
      config.params = {}
      config.url = url
    }

    // 토큰 검증이 필요 없는 요청 (로그인, 토큰 갱신 API)
    if (
      config.url?.includes('userLogin') ||
      config.url?.includes('commonTokenInfo') ||
      config.url?.includes('userPopupInfo') ||
      config.url?.includes('getSingleExamData') ||
      config.url?.includes('statusExamDataWebPrint') ||
      config.url?.includes('userJoinInfo') ||
      config.url?.includes('userJoinSms')
    ) {
      const app = useAppStore()
      if (app.user != null && app.user.JADOO_TOKEN != null) {
        const token = app.user.JADOO_TOKEN
        if (token) {
          config.headers.Authorization = 'Bearer ' + token
        }
      }

      return config
    }

    // 토큰 검증 및 갱신 로직
    const { validateTokens } = useTokenValidation()
    const result = validateTokens()

    // 토큰이 없거나 유효하지 않은 경우 → 로그아웃
    if (
      //result.status === 'missing-tokens' ||
      result.status === 'invalid-tokens'
    ) {
      devConsole.log(
        '토큰 없음 또는 유효하지 않음 - 로그아웃 처리:',
        result.status,
      )
      //logoutAndCleanData()
      //return Promise.reject(new Error('Authentication required'))
    }

    // 액세스 토큰만 만료된 경우 → 갱신
    if (result.status === 'access-expired') {
      devConsole.log('액세스 토큰 만료 - 갱신 시작')

      // 이미 갱신 중이면 그 Promise를 재사용
      if (!refreshTokenPromise) {
        const app = useAppStore()
        refreshTokenPromise = commonRefreshToken({
          UM_OAUTH_CD: app.user!.UM_OAUTH_CD,
          ACCESS_TOKEN: app.user!.ACCESS_TOKEN,
          REFRESH_TOKEN: app.user!.REFRESH_TOKEN,
          JADOO_TOKEN: app.user!.JADOO_TOKEN,
        })
          .then((res) => {
            if (res?.data?.code === '200') {
              // 토큰 업데이트
              const app = useAppStore()
              app.user!.ACCESS_TOKEN = res.data.data.ACCESS_TOKEN
              app.user!.REFRESH_TOKEN = res.data.data.REFRESH_TOKEN
              app.user!.JADOO_TOKEN = res.data.data.JADOO_TOKEN
              devConsole.log('토큰 갱신 성공')
              return res.data.data
            } else {
              devConsole.error('토큰 갱신 실패 - 로그아웃 처리')
              logoutAndCleanData()
              throw new Error('Token refresh failed')
            }
          })
          .catch((error) => {
            devConsole.error('토큰 갱신 중 에러 발생:', error)
            logoutAndCleanData()
            throw error
          })
          .finally(() => {
            refreshTokenPromise = null // 완료되면 초기화
          })
      }

      // 갱신 완료 대기 (첫 요청이든 후속 요청이든 모두 같은 Promise를 await)
      const newTokens = await refreshTokenPromise
      config.headers.Authorization = 'Bearer ' + newTokens.JADOO_TOKEN
      return config
    }

    // 토큰 유효 → 정상 진행
    const app = useAppStore()
    if (app.user != null && app.user.JADOO_TOKEN != null) {
      const token = app.user.JADOO_TOKEN
      if (token) {
        config.headers.Authorization = 'Bearer ' + token
      }
    }

    return config
  },
  (error: AxiosError) => {
    // Do something with request error
    devConsole.error('Request interceptor error:', error)
    return Promise.reject(error)
  },
)

// response 拦截器
service.interceptors.response.use(
  (response: AxiosResponse<any>) => {
    if (response.config.responseType === 'blob') {
      // 如果是文件流，直接过
      return response
    } else if (response.data.code === result_code) {
      return response
    } else if (
      response.data.code === token_missing_code || // 301
      response.data.code === token_error_reject_code || // 303
      response.data.code === reject_code // 304
    ) {
      // 토큰 관련 에러 발생 시 처리
      devConsole.log('토큰 관련 에러 응답:', response.data.code)
      logoutAndCleanData()
      return Promise.reject(response)
    }
    return response
  },
  (error: AxiosError) => {
    devConsole.error('Response error:', error)
    if (error.status === 409) {
      return error.response
    }
    return Promise.reject(error)
  },
)

export { service, serviceMock }
